import React, { useState } from 'react';
import { useDynamicList } from 'ahooks';
import _ from 'lodash';

import { List } from 'src/components/custom';
import Form from 'src/components/form';
import {
  RadioWithStatus,
  CheckboxWithStatus,
  Typography,
} from 'src/components/coreUI';
import { TouchedQuiz } from 'src/thunks/module_quiz';
import BlockNavigate, {
  Props as BlockProps,
} from 'src/components/applications/BlockNavigate';

type Props = {
  questions?: Model.Question[];
  renderAction?: (
    answers: TouchedQuiz[],
    handleValidate: () => boolean,
    handleResetAll: () => void,
    canRetry: boolean,
  ) => React.ReactNode;
  blockProps?: BlockProps;
  labelForAssessment?: boolean;
  bold?: boolean;
};

const Quizzes: React.FC<Props> = ({
  renderAction,
  questions = [],
  blockProps,
  labelForAssessment,
  bold = true,
}) => {
  const defaultList = questions.map((q) => ({
    question_id: q.id,
    answer: q.user_answer || { value: [] },
  }));

  const touchedQuiz = useDynamicList<TouchedQuiz>(defaultList);

  const [errors, setErrors] = useState<string[]>([]);

  const canRetry = questions?.some((q) => {
    return !_.isEqual(q.answer, q.user_answer);
  });

  const validateEmptyAnswers = () => {
    const emptyQuiz = touchedQuiz.list.filter(
      (a) => a.answer.value.length === 0,
    );

    if (emptyQuiz.length) {
      const errorIds = emptyQuiz.map((q) => q.question_id);
      setErrors(errorIds);

      const firstError = errorIds[0];
      const el = document.getElementById(firstError);

      el?.scrollIntoView({ behavior: 'smooth' });

      return true;
    }

    return false;
  };

  const handleResetError = (id: string) => {
    if (errors.includes(id)) {
      setErrors(errors.filter((e) => e !== id));
    }
  };

  return (
    <React.Fragment>
      <List
        columns={1}
        data={questions}
        renderItem={(q, index) => {
          const getOptions = () => {
            if (q.options?.value) {
              const optEntries = Object.entries(q.options.value);
              return optEntries.map(([key, value]) => {
                return {
                  label: value,
                  value: key,
                };
              });
            }

            return [];
          };

          const key = touchedQuiz.getKey(index);

          const userAnswersFromServer = q.user_answer?.value;
          const severAnswers = q.answer?.value;
          const currentUserAnswers = touchedQuiz.list[key]?.answer.value || [];

          const errorMessage = errors.includes(q.id)
            ? 'Please choose an answer'
            : undefined;

          const validateStatus = (option: string) => {
            if (!severAnswers) return undefined;

            if (
              userAnswersFromServer &&
              severAnswers.includes(option) &&
              userAnswersFromServer.includes(option)
            ) {
              return true;
            }

            if (
              userAnswersFromServer &&
              !severAnswers.includes(option) &&
              userAnswersFromServer.includes(option)
            ) {
              return false;
            }
          };

          return (
            <Form>
              {labelForAssessment && (
                <Typography.Title>
                  Question {`${index + 1} / ${questions?.length}`}
                </Typography.Title>
              )}
              <div id={q.id} className={`my-24 ${bold ? 'fw-bold' : ''}`}>
                {q.title}
              </div>

              {q.type === 'radio_button' && (
                <Form.RadioGroup
                  options={getOptions()}
                  error={errorMessage}
                  value={q.answer?.value}
                  renderItem={(option, checked) => {
                    return (
                      <RadioWithStatus
                        label={option.label}
                        value={option.value}
                        // defaultAnswer={q.answer?.value}
                        onChange={() => {
                          if (userAnswersFromServer) return;

                          handleResetError(q.id);

                          touchedQuiz.replace(key, {
                            question_id: q.id,
                            answer: {
                              value: [option.value],
                            },
                          });
                        }}
                        checked={(
                          userAnswersFromServer || currentUserAnswers
                        ).includes(option.value)}
                        status={validateStatus(option.value)}
                      />
                    );
                  }}
                />
              )}

              {q.type === 'checkboxes' && (
                <Form.RadioGroup
                  options={getOptions()}
                  error={errorMessage}
                  value={q.answer?.value}
                  renderItem={(option, checked) => {
                    const answersChosen =
                      touchedQuiz.list[key]?.answer?.value || [];
                    const hasChosen = answersChosen.includes(option.value);

                    return (
                      <CheckboxWithStatus
                        label={option.label}
                        value={option.value}
                        // defaultAnswer={q.answer?.value}
                        onChange={() => {
                          if (userAnswersFromServer) return;
                          handleResetError(q.id);

                          touchedQuiz.replace(key, {
                            question_id: q.id,
                            answer: {
                              value: hasChosen
                                ? answersChosen.filter(
                                    (a) => a !== option.value,
                                  )
                                : [...answersChosen, option.value],
                            },
                          });
                        }}
                        checked={hasChosen}
                        status={validateStatus(option.value)}
                      />
                    );
                  }}
                />
              )}
            </Form>
          );
        }}
      />
      {renderAction?.(
        touchedQuiz.list,
        validateEmptyAnswers,
        () => touchedQuiz.resetList(defaultList),
        canRetry,
      )}

      <BlockNavigate
        confirmProps={{
          title: 'BUT WAIT, THERE’S A PIT STOP!',
          iconProps: {
            name: 'warning',
            color: 'yellow',
          },
          content:
            'Please answer before proceeding to the next section. Your progress will only be counted once you have finished.',
          cancelText: 'Answer pit stop',
          okText: 'Continue anyway',
          okButtonProps: {
            basic: true,
            fluid: true,
          },
          cancelButtonProps: {
            primary: true,
            fluid: true,
          },
        }}
        {...blockProps}
      />
    </React.Fragment>
  );
};

export default Quizzes;
